import _debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { S3, WIDGET_ICONS } from "../../../../../../constants";
import useCompareState from "../../../../../../hooks/useCompareState";
import ImageRadio from "../../../../../../parts/inputs/image-radio";
import ThumbnailUploadInput from "../../../../../../parts/inputs/thumbnail-upload";
import { EditWidgetInfoModal } from "../../../../../../parts/modals/EditWidgetInfo";
import SvgIcon from "../../../../../../parts/SvgIcon";
import scriptLoader from "../../../../../../utils/scriptLoader";
import { isPullActiveCoach } from "../../../../../../utils/user-helpers";
import {
  deleteWidgetUtil,
  orderWidgets,
} from "../../../../../../utils/widgets";
import { getUserToRequest } from "../../../../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { EditLink } from "./EditLink";
import WidgetManagerHeader from "./WidgetManagerHeader";

export const LinkWidgetManager = ({ locals, goBack }) => {
  const { t } = useTranslation("coaching-portal");
  const activeWidget = locals.activeWidget;
  const widgetLinks = locals.widgetLinks;
  const widgetLinkThumbnail = locals.widgetLinkThumbnail;
  const activeUser = locals.activeUser;
  const organizationOwner = locals.organizationOwner;
  const userToRequest = getUserToRequest(activeUser, organizationOwner);

  const [formState, setFormState] = useState({}); //state to control the widget's form
  const [urlState, setUrlState] = useState({ url: "", widget: "", _id: "" }); //state to control the link's edit form
  const [addNew, setAddNew] = useState("addNewButton"); //state to control if the "add new link" section is opened or not
  const [statusState, setStatusState] = useState("initial"); //state to control if the links are in "change order" mode or not
  const [widgetLinkState, setWidgetLinkState] = useState([]); //state to link the the link of the widget
  const [editLinkState, setEditLinkState] = useState(""); //state to link the current link to can control the accordions
  //state to control the link's add new form
  const [linkFormState, setLinkFormState] = useState({
    url: "",
  });
  const [refreshList, setRefreshList] = useState(false);
  const [changedOrder, setChangedOrder] = useState(false);
  const [editWidgetInfoModalOpened, setEditWidgetInfoModalOpened] =
    useState(false);

  //memorize the debounce function to avoid recreate it again when the component rerenders
  const debounceFn = useCallback(_debounce(handleDebounceFn, 2000), []);

  /**
   * Reorder
   */
  useEffect(() => {
    let sortable = null;
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-wrapper",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                const ordered = Array.from(items).map((element) => {
                  const urlInput = element.querySelector(".url-section-div");
                  const idInput = element.querySelector(".id-section-div");
                  const imageInput =
                    element.querySelector(".image-section-div");
                  const thumbnailInput = element.querySelector(
                    ".thumbnail-section-div"
                  );

                  if (imageInput !== null) {
                    //if there is an image
                    return {
                      url: (urlInput && urlInput.href) || "",
                      widget: activeWidget._id,
                      _id: (idInput && idInput.value) || "",
                      image: (imageInput && JSON.parse(imageInput.value)) || "",
                    };
                  } else {
                    //if there is a thumbnail
                    return {
                      url: (urlInput && urlInput.href) || "",
                      widget: activeWidget._id,
                      _id: (idInput && idInput.value) || "",
                      thumbnail: thumbnailInput.currentSrc
                        ? thumbnailInput.currentSrc
                        : undefined,
                    };
                  }
                });
                setWidgetLinkState(ordered);
                setChangedOrder(true);
                return false;
              },
            }
          );
        }
      );
    }
    return () => {
      if (sortable) sortable.destroy();
    };
  }, []);

  /**
   * Close the "add new link" section
   */
  useEffect(() => {
    if (locals.query.submited) {
      setLinkFormState({ url: "" });
      setAddNew("addNewButton");
      setStatusState("initial");
      setEditLinkState("");
    }
  }, [locals.query]);

  /**
   * Set the values to the states
   */
  const [compareBase, setCompareBase, setCurrentCompareState, hasChanged] =
    useCompareState();
  useEffect(() => {
    if (!!activeWidget) {
      const comparingBase = {
        name: activeWidget.name,
        description: activeWidget.description,
        icon: activeWidget.icon,
        pushPermission:
          activeWidget.pushPermission === undefined
            ? true
            : activeWidget.pushPermission,
        pullPermission:
          activeWidget.pullPermission === undefined
            ? true
            : activeWidget.pullPermission,
      };
      setFormState({
        ...comparingBase,
        id: activeWidget._id,
      });
      setCompareBase(comparingBase);
      setCurrentCompareState(comparingBase);
    }
    if (!!widgetLinks) {
      setWidgetLinkState(widgetLinks);
    }
  }, [activeWidget, widgetLinks]);

  const {
    description = "",
    name = "",
    icon = "",
    id = 0,
    pushPermission = true,
    pullPermission = true,
  } = formState;

  /**
   * Validate if the widget's form is valid
   * @returns
   */
  const validationSubmit = () => {
    if (description.length > 0 && name.length > 0 && icon.length > 0) {
      return true;
    }
    return false;
  };

  /**
   * Delete a link
   * @param {*} e
   * @param {*} id
   */
  const handleDelete = (e, id) => {
    console.log("handleDelete -> id", id);
    if (
      process.browser &&
      confirm(t("widgets.linkManager.confirmDeleteLink"))
    ) {
      e.stopPropagation();
      e.preventDefault();
      submit(`/actions/links/delete/${id}`, {
        method: "GET",
      });
    }
  };

  const handleInputChange = ({ target }) => {
    const targetName = target?.name.replace("-mobile", "") || target?.name;
    const newState = Object.assign({}, formState, {
      [targetName]: target.value,
    });
    setFormState(newState);
    setCurrentCompareState({
      ...compareBase,
      [targetName]: target.value,
    });
  };

  /**
   * Edit a link
   * @param {*} e
   */
  const handleEditSubmit = (e) => {
    e.preventDefault();
    setEditLinkState("");
    setStatusState("initial");
    submit(`/actions/links/save/${urlState._id}`, {
      method: "POST",
      body: urlState,
    });
  };

  /**
   * Add a link
   * @param {*} e
   */
  const handleSubmit = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const body = formState;
    delete body.order;
    delete body.status;
    delete body.type;
    delete body.id;

    submit(`/actions/widgets/save/${id}`, {
      method: "POST",
      body: body,
    });
  };

  /**
   * Control if the "add new link" section is opened or not
   */
  const handleChangeAddNewState = () => {
    setAddNew("addNewForm");
  };

  /**
   * Control if the links are in "edit mode" or not
   * @param {*} state
   */
  const handleChangeEditState = (state) => {
    setStatusState(state);
  };

  /**
   * Delete a widget
   * @param {*} e
   */
  const deleteWidget = (e) => {
    if (!!activeWidget) {
      const { _id } = activeWidget;
      deleteWidgetUtil(e, _id);
    }
  };

  /**
   * Reorder
   * @param {*} e
   */
  const handleSaveReorder = (e) => {
    e.preventDefault();
    const orderedLinks = widgetLinkState.map((element) => element._id);

    if (orderedLinks.length > 0) {
      setStatusState("initial");
      submit("/actions/links/reorder", {
        method: "POST",
        body: { orderedLinks },
      });
    }
  };
  const cancelReorder = () => {
    setWidgetLinkState(widgetLinks);
    setStatusState("initial");
    setRefreshList(!refreshList);
  };
  /**
   * Control if the links are in "edit mode" or not
   * @param {*} state
   */
  const handleChangeStatusState = (state) => {
    setStatusState(state);
  };

  /**
   * Validate if edit form is valid
   * @returns
   */
  const validationLinkSubmit = () => {
    if (linkFormState.url.length > 0 && !!widgetLinkThumbnail) {
      return true;
    }
    return false;
  };

  /**
   * Refresh the page cleaning the submited query
   */
  const handleRefresh = () => {
    submit(`/actions/refresh/`, {
      method: "GET",
      body: { submited: "", thumbnail: "" },
    });
  };

  /**
   * assign the value to the add new link state
   * @param {*} param0
   */
  const handleInputLinkChange = (event) => {
    const newState = Object.assign({}, linkFormState, {
      [event.target.name]: event.target.value,
    });
    setLinkFormState(newState);

    searchThumbnail(event);
  };

  /**
   * search thumbnail when user enter a url
   */
  const searchThumbnail = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const URL_REGEX =
      /^(?:(?:https?):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i;

    if (!URL_REGEX.test(e.target.value)) {
      return;
    }

    debounceFn(e.target.value);
  };

  function handleDebounceFn(inputValue) {
    submit(`/actions/links/thumbnail`, {
      method: "POST",
      body: { url: inputValue },
    });
  }

  /**
   * Open the edit widget info modal (only for mobile)
   */
  const onOpenEditWidgetInfo = () => {
    setEditWidgetInfoModalOpened(!editWidgetInfoModalOpened);
  };

  /**
   * Control access user
   * @param {*} param0
   * @returns
   */

  const WidgetTogglePush = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.subscriptionsPush")}</span>
      <input
        type="checkbox"
        id="switch-1"
        name="switch-1"
        role="switch"
        onChange={() => {
          const newState = Object.assign({}, formState, {
            pushPermission: !formState.pushPermission,
          });
          setFormState(newState);
          setCurrentCompareState({
            ...compareBase,
            pushPermission: !formState.pushPermission,
          });
        }}
        checked={permission ? true : undefined}
      />
    </div>
  );

  const WidgetTogglePull = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.coachingPull")}</span>
      <input
        type="checkbox"
        id="switch-1"
        name="switch-1"
        role="switch"
        onChange={() => {
          const newState = Object.assign({}, formState, {
            pullPermission: !formState.pullPermission,
          });
          setFormState(newState);
          setCurrentCompareState({
            ...compareBase,
            pullPermission: !formState.pullPermission,
          });
        }}
        checked={permission ? true : undefined}
      />
    </div>
  );

  return (
    <main className="widgets-manager list">
      <WidgetManagerHeader
        name={name}
        description={description}
        goBack={goBack}
        onOpenEditWidgetInfo={onOpenEditWidgetInfo}
      />
      <EditWidgetInfoModal
        widgetName={formState.name}
        widgetDescription={formState.description}
        widgetIcon={formState.icon}
        isOpen={editWidgetInfoModalOpened}
        onClose={onOpenEditWidgetInfo}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        validationSubmit={validationSubmit}
        iconsRadios={WIDGET_ICONS["links"]}
        WidgetTogglePull={WidgetTogglePull}
        WidgetTogglePush={WidgetTogglePush}
        userToRequest={userToRequest}
        pullPermission={pullPermission}
        pushPermission={pushPermission}
      />
      <div>
        {!!activeWidget && (
          <aside className="mobile-hidden">
            <div className="overflow-auto max-h-[calc(100vh-16.75rem)]">
              <label>{t("widgets.common.widgetName")}</label>
              <input
                maxLength="30"
                name="name"
                className="text-input"
                placeholder={t("widgets.common.enterName")}
                value={name}
                onChange={handleInputChange}
              />
              <label>{t("widgets.common.widgetDescription")}</label>
              <textarea
                maxLength="60"
                name="description"
                className="text-input"
                placeholder={t("widgets.common.enterDescriptionHere")}
                value={description}
                onChange={handleInputChange}
              />
              <ImageRadio
                label={t("widgets.common.widgetIcon")}
                name="icon"
                currentValue={icon}
                radios={WIDGET_ICONS["links"]}
                onChange={handleInputChange}
                isManager={true}
              />
              <label>{t("widgets.common.userTypeAccess")}</label>
              {isPullActiveCoach(userToRequest) && (
                <WidgetTogglePull permission={pullPermission} />
              )}
              {userToRequest.pushAccountInfo && (
                <WidgetTogglePush permission={pushPermission} />
              )}
            </div>
            <footer>
              <button
                data-button-type="secondary"
                data-action="cancel"
                onClick={deleteWidget}
              >
                {t("widgets.common.delete")}
              </button>
              <button
                data-button-type="primary"
                onClick={handleSubmit}
                disabled={!hasChanged}
              >
                {t("widgets.common.save")}
              </button>
            </footer>
          </aside>
        )}
        <section>
          <header>
            <section className="only-show-mobile back-handler">
              <SvgIcon
                src={"/img/new-icons/back-button.svg"}
                onClickFn={() =>
                  submit(`/actions/refresh/?`, {
                    method: "GET",
                    body: { section: "", learn: "" },
                  })
                }
              />
              <h3>{activeWidget?.name || t("widgets.common.explore")}</h3>
            </section>
            <hgroup className="mobile-hidden">
              <h4>{t("widgets.linkManager.urlLinks")}</h4>
              <p className="p6">{t("widgets.linkManager.description")}</p>
            </hgroup>
            {statusState === "initial" ? (
              <button
                data-button-type="secondary"
                onClick={() => handleChangeStatusState("change-order")}
              >
                {t("widgets.common.changeOrder")}
              </button>
            ) : (
              <hgroup className="actions-wrapper">
                <button
                  data-button-type="primary"
                  onClick={
                    statusState === "change-order"
                      ? handleSaveReorder
                      : handleEditSubmit
                  }
                  disabled={!changedOrder}
                >
                  {t("widgets.common.save")}
                </button>
                {statusState === "change-order" && (
                  <button
                    data-button-type="secondary"
                    data-action="cancel"
                    onClick={cancelReorder}
                  >
                    {t("widgets.common.cancel")}
                  </button>
                )}
              </hgroup>
            )}
          </header>
          <div className="principal-grabber-div">
            {!!widgetLinks &&
              orderWidgets(widgetLinkState).map(
                ({ _id, url, widget, thumbnail, image }) => {
                  const imageToShow =
                    image && Object.keys(image).length > 0 ? image : undefined;
                  return editLinkState !== _id ||
                    statusState === "change-order" ? (
                    <div className="article-wrapper link">
                      <article
                        key={`${_id}-${refreshList}`}
                        id={_id}
                        className={`item-grabber-container${
                          statusState === "change-order" ? " column" : ""
                        }`}
                      >
                        <div
                          className={
                            thumbnail
                              ? "link-image-thumbnail-section"
                              : "link-image-no-thumbnail-section"
                          }
                        >
                          <input
                            className="id-section-div"
                            name="id"
                            style={{ display: "none" }}
                            readOnly
                            value={_id}
                          />
                          {/* if have thumbnail but no image */}
                          {thumbnail && !imageToShow ? (
                            <img
                              className="thumbnail-section-div"
                              src={thumbnail}
                              loading="lazy"
                            />
                          ) : // if dont have thumbnail and have image
                          !thumbnail && imageToShow ? (
                            <div>
                              <img
                                src={S3.toCDN(image.s3Path)}
                                loading="lazy"
                              />
                              <input
                                className="image-section-div"
                                name="image"
                                style={{ display: "none" }}
                                readOnly
                                value={JSON.stringify(image)}
                              />
                            </div>
                          ) : // if  have thumbnail and have image

                          thumbnail && imageToShow ? (
                            <div>
                              <img
                                src={S3.toCDN(image.s3Path)}
                                loading="lazy"
                              />
                              <input
                                className="image-section-div"
                                name="image"
                                style={{ display: "none" }}
                                readOnly
                                value={JSON.stringify(image)}
                              />
                            </div>
                          ) : (
                            // if dont have thumbnail and dont have image
                            <div className="thumbnail-image-div">
                              <p className="p4">
                                {t(
                                  "widgets.generalResources.thumbnailNotAvailable"
                                )}
                              </p>
                            </div>
                          )}
                          <a
                            target="_blank"
                            rel="noopener"
                            href={url}
                            className="explore-card-title url-section-div"
                          >
                            {url}
                          </a>
                        </div>

                        <div className="action-buttons">
                          <button
                            onClick={() => {
                              setUrlState({
                                url: url,
                                widget: widget,
                                _id: _id,
                              });
                              handleChangeEditState("edit");
                              setEditLinkState(_id);
                              handleRefresh();
                            }}
                            data-button-type="secondary"
                            data-icon-only
                          >
                            <SvgIcon
                              width={20}
                              height={20}
                              src="/img/new-icons/edit-pencil.svg"
                              alt="delete"
                            />
                          </button>

                          <button
                            data-button-type="secondary"
                            data-icon-only
                            onClick={(event) => handleDelete(event, _id)}
                          >
                            <SvgIcon
                              width={20}
                              height={20}
                              src="/img/new-icons/red-trash.svg"
                              alt="delete"
                              noFilterOnLight={true}
                            />
                          </button>
                        </div>
                      </article>
                      {statusState === "change-order" && (
                        <div className="drag-wrapper">
                          <SvgIcon
                            src="/img/new-icons/menu.svg"
                            className="drag-handle"
                            mobileSrc="/img/new-icons/drag.svg"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <EditLink
                      urlProps={url}
                      widgetProps={widget}
                      _idProps={_id}
                      thumbnailProps={thumbnail}
                      imageProps={imageToShow}
                      locals={locals}
                      setEditLinkState={setEditLinkState}
                    />
                  );
                }
              )}
          </div>
          {addNew === "addNewButton" ? (
            <div
              className="dashed-box"
              onClick={() => {
                handleChangeAddNewState();
                handleRefresh();
              }}
            >
              <SvgIcon src="/img/new-icons/add-square-outline.svg" alt="back" />

              <span>{t("widgets.linkManager.addNewLink")}</span>
            </div>
          ) : (
            <form
              encType="multipart/form-data"
              method="POST"
              data-noreset
              action={`/actions/links/save/`}
              className="add-edit-store-wrapper"
            >
              <ThumbnailUploadInput
                widgetLinkThumbnail={widgetLinkThumbnail}
                name="image"
              />
              <div>
                <div className="input-box">
                  <label> {t("widgets.editLink.linkUrl")} </label>
                  <input
                    name="url"
                    className="text-input"
                    placeholder={t("widgets.editLink.pasteUrlPlaceholder")}
                    value={linkFormState.url}
                    onChange={handleInputLinkChange}
                  />
                  <input
                    name="widget"
                    style={{ display: "none" }}
                    defaultValue={id}
                  />
                </div>
                <div className="flex flex-row gap-2">
                  <button
                    data-button-type="primary"
                    disabled={!validationLinkSubmit()}
                  >
                    {t("widgets.common.save")}
                  </button>
                  <button
                    data-button-type="text-white"
                    data-action="cancel"
                    onClick={(e) => {
                      e.preventDefault();
                      setAddNew("addNewButton");
                    }}
                  >
                    {t("widgets.common.cancel")}
                  </button>
                </div>
              </div>
            </form>
          )}
        </section>
      </div>
    </main>
  );
};
